import * as React from "react";
import { AiOutlineCheck, AiOutlineDown, AiOutlinePlus } from "react-icons/ai";
import { RiExpandUpDownLine } from "react-icons/ri";

const SearchableSelect = ({
  options,
  placeholder,
  onSelect,
  disabled = false,
  className = "",
  resetOnSelect = true,
  value: externalValue,
  grouped = false,
  groupLabels = {},
}) => {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const containerRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const [placeUp, setPlaceUp] = React.useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = React.useState(300);

  // Use external value if provided, otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue;

  const handleSelect = (currentValue) => {
    if (resetOnSelect) {
      setInternalValue(""); // Reset the value to show placeholder
      setSearchValue(""); // Reset search value
    } else {
      setInternalValue(currentValue); // Keep the selected value
      setSearchValue(""); // Reset search value
    }
    setOpen(false);
    onSelect(currentValue);
  };

  const handleAddCustom = () => {
    if (searchValue.trim()) {
      handleSelect(searchValue.trim());
    }
  };

  const computePlacement = React.useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const desired = 300;
    const shouldPlaceUp = spaceBelow < 200 && spaceAbove > spaceBelow;
    setPlaceUp(shouldPlaceUp);
    const maxH = Math.max(
      120,
      Math.min(shouldPlaceUp ? spaceAbove : spaceBelow, desired)
    );
    setDropdownMaxHeight(maxH);
  }, []);

  React.useEffect(() => {
    if (!open) return;
    computePlacement();
    const onScroll = () => computePlacement();
    const onResize = () => computePlacement();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, computePlacement]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Group options if grouped is true
  const groupOptions = (options) => {
    if (!grouped) return { All: options };

    const groups = {};

    options.forEach((option) => {
      // Determine group based on skill type
      let group = "Other";

      if (
        [
          "JavaScript",
          "TypeScript",
          "Python",
          "Java",
          "C++",
          "C#",
          "PHP",
          "Ruby",
          "Go",
          "Rust",
          "Swift",
          "Kotlin",
          "Dart",
          "Scala",
          "R",
          "MATLAB",
          "Perl",
          "Haskell",
          "Elixir",
          "Clojure",
          "F#",
          "Objective-C",
          "Assembly",
          "COBOL",
          "Fortran",
        ].includes(option)
      ) {
        group = "Programming Languages";
      } else if (
        [
          "React",
          "Vue.js",
          "Angular",
          "Svelte",
          "Next.js",
          "Nuxt.js",
          "Gatsby",
          "Remix",
          "Solid.js",
          "Alpine.js",
          "Ember.js",
          "Backbone.js",
          "jQuery",
          "HTML",
          "CSS",
          "SASS",
          "SCSS",
          "Less",
          "Stylus",
          "Tailwind CSS",
          "Bootstrap",
          "Material-UI",
          "Ant Design",
          "Chakra UI",
          "Mantine",
          "Headless UI",
          "Radix UI",
          "Framer Motion",
          "Three.js",
          "WebGL",
          "Canvas API",
          "Web Workers",
          "Service Workers",
          "Progressive Web Apps",
          "WebAssembly",
        ].includes(option)
      ) {
        group = "Frontend Technologies";
      } else if (
        [
          "Node.js",
          "Express.js",
          "NestJS",
          "Fastify",
          "Koa",
          "Django",
          "Flask",
          "FastAPI",
          "Spring Boot",
          "Spring MVC",
          "Laravel",
          "CodeIgniter",
          "Symfony",
          "ASP.NET",
          "ASP.NET Core",
          "Blazor",
          "Ruby on Rails",
          "Sinatra",
          "Phoenix",
          "Gin",
          "Echo",
          "Fiber",
          "Actix",
          "Rocket",
          "Axum",
          "Full Stack Development",
          "MERN Stack",
          "MEAN Stack",
          "LAMP Stack",
          "JAMstack",
          "T3 Stack",
          "PERN Stack",
          "MERN Stack",
          "MEVN Stack",
        ].includes(option)
      ) {
        group = "Backend & Full Stack";
      } else if (
        [
          "MongoDB",
          "PostgreSQL",
          "MySQL",
          "SQLite",
          "SQL Server",
          "Oracle",
          "Redis",
          "Cassandra",
          "DynamoDB",
          "Firebase",
          "Supabase",
          "PlanetScale",
          "Neon",
          "CockroachDB",
          "InfluxDB",
          "Elasticsearch",
          "Solr",
          "Neo4j",
          "ArangoDB",
          "GraphQL",
          "REST API",
          "gRPC",
          "GraphQL",
          "Apollo",
          "Prisma",
          "Sequelize",
          "TypeORM",
          "Mongoose",
          "Drizzle",
          "Kysely",
          "Data Analysis",
          "Data Science",
          "Machine Learning",
          "Deep Learning",
          "TensorFlow",
          "PyTorch",
          "Scikit-learn",
          "Pandas",
          "NumPy",
          "Matplotlib",
          "Seaborn",
          "Jupyter",
          "Big Data",
          "Hadoop",
          "Spark",
          "Kafka",
          "Airflow",
          "dbt",
        ].includes(option)
      ) {
        group = "Databases & Data";
      } else if (
        [
          "AWS",
          "Azure",
          "Google Cloud",
          "DigitalOcean",
          "Heroku",
          "Vercel",
          "Netlify",
          "Railway",
          "Render",
          "Fly.io",
          "Docker",
          "Kubernetes",
          "Docker Compose",
          "Helm",
          "Terraform",
          "Ansible",
          "Chef",
          "Puppet",
          "Jenkins",
          "GitHub Actions",
          "GitLab CI",
          "CircleCI",
          "Travis CI",
          "ArgoCD",
          "Flux",
          "Prometheus",
          "Grafana",
          "ELK Stack",
          "Splunk",
          "New Relic",
          "Datadog",
          "CloudFormation",
          "Pulumi",
          "Serverless",
          "Lambda",
          "Functions",
          "Microservices",
          "API Gateway",
          "Load Balancing",
          "CDN",
          "Edge Computing",
        ].includes(option)
      ) {
        group = "Cloud & DevOps";
      } else if (
        [
          "React Native",
          "Flutter",
          "Ionic",
          "Xamarin",
          "NativeScript",
          "Cordova",
          "PhoneGap",
          "iOS Development",
          "Android Development",
          "SwiftUI",
          "Jetpack Compose",
          "UIKit",
          "Android SDK",
          "Xcode",
          "Android Studio",
          "Mobile App Development",
          "Cross-platform Development",
          "Hybrid App Development",
          "Progressive Web Apps",
        ].includes(option)
      ) {
        group = "Mobile Development";
      } else if (
        [
          "Jest",
          "Cypress",
          "Selenium",
          "Playwright",
          "Puppeteer",
          "Testing Library",
          "Vitest",
          "Mocha",
          "Chai",
          "Sinon",
          "Jasmine",
          "Karma",
          "Protractor",
          "Appium",
          "Detox",
          "Unit Testing",
          "Integration Testing",
          "End-to-End Testing",
          "Test-Driven Development",
          "Behavior-Driven Development",
          "Performance Testing",
          "Load Testing",
          "Security Testing",
          "Accessibility Testing",
          "SonarQube",
          "Code Coverage",
          "Mutation Testing",
        ].includes(option)
      ) {
        group = "Testing & Quality";
      } else if (
        [
          "Git",
          "GitHub",
          "GitLab",
          "Bitbucket",
          "SVN",
          "Webpack",
          "Vite",
          "Rollup",
          "Parcel",
          "Babel",
          "TypeScript Compiler",
          "ESLint",
          "Prettier",
          "Stylelint",
          "Husky",
          "Lint-staged",
          "Commitizen",
          "Conventional Commits",
          "Semantic Release",
          "Storybook",
          "Docusaurus",
          "VitePress",
          "JSDoc",
          "Swagger",
          "Postman",
          "Insomnia",
          "Thunder Client",
          "Figma",
          "Adobe XD",
          "Sketch",
          "InVision",
          "Zeplin",
          "Notion",
          "Confluence",
          "Slack",
          "Discord",
          "Microsoft Teams",
          "Zoom",
          "Google Meet",
        ].includes(option)
      ) {
        group = "Tools & Utilities";
      } else if (
        [
          "Agile",
          "Scrum",
          "Kanban",
          "Lean",
          "DevOps",
          "CI/CD",
          "Continuous Integration",
          "Continuous Deployment",
          "Continuous Delivery",
          "Blue-Green Deployment",
          "Canary Deployment",
          "Feature Flags",
          "A/B Testing",
          "Design Systems",
          "Component Libraries",
          "Design Patterns",
          "SOLID Principles",
          "Clean Architecture",
          "Domain-Driven Design",
          "Event-Driven Architecture",
          "Microservices Architecture",
          "Monolithic Architecture",
          "Serverless Architecture",
          "Event Sourcing",
          "CQRS",
          "Hexagonal Architecture",
        ].includes(option)
      ) {
        group = "Methodologies & Frameworks";
      } else if (
        [
          "Leadership",
          "Project Management",
          "Team Management",
          "Product Management",
          "Business Analysis",
          "Requirements Gathering",
          "User Experience Design",
          "User Interface Design",
          "Information Architecture",
          "Wireframing",
          "Prototyping",
          "User Research",
          "Usability Testing",
          "A/B Testing",
          "Conversion Optimization",
          "SEO",
          "Content Marketing",
          "Digital Marketing",
          "Social Media Marketing",
          "Email Marketing",
          "Analytics",
          "Google Analytics",
          "Mixpanel",
          "Amplitude",
          "Hotjar",
          "Customer Success",
          "Sales",
          "Business Development",
          "Strategic Planning",
          "Risk Management",
          "Change Management",
          "Stakeholder Management",
          "Conflict Resolution",
          "Negotiation",
          "Presentation Skills",
          "Public Speaking",
          "Technical Writing",
          "Documentation",
          "Training",
          "Mentoring",
          "Code Review",
          "Pair Programming",
          "Mob Programming",
          "Remote Work",
          "Time Management",
          "Problem Solving",
          "Critical Thinking",
          "Creativity",
          "Innovation",
          "Adaptability",
          "Flexibility",
          "Collaboration",
          "Communication",
          "Active Listening",
          "Empathy",
          "Emotional Intelligence",
          "Cultural Awareness",
          "Diversity & Inclusion",
          "Ethics",
          "Sustainability",
          "Corporate Social Responsibility",
        ].includes(option)
      ) {
        group = "Soft Skills & Business";
      } else if (
        [
          "Customer Support",
          "Account Management",
          "Lead Generation",
          "Inside Sales",
          "Field Sales",
          "B2B Sales",
          "B2C Sales",
          "Sales",
        ].includes(option)
      ) {
        group = "Sales & Customer";
      } else if (
        [
          "Talent Acquisition",
          "Recruitment",
          "Interviewing",
          "Onboarding",
          "HR Operations",
          "Payroll",
        ].includes(option)
      ) {
        group = "HR & People Ops";
      } else if (
        [
          "Operations Management",
          "Office Administration",
          "Procurement",
          "Supply Chain",
        ].includes(option)
      ) {
        group = "Operations & Admin";
      } else if (
        [
          "Training & Development",
          "Instructional Design",
          "Teaching",
          "Counseling",
        ].includes(option)
      ) {
        group = "Education & Training";
      } else if (
        [
          "Public Relations",
          "Brand Management",
          "Community Management",
          "Community Outreach",
          "Event Management",
          "Canva",
        ].includes(option)
      ) {
        group = "Marketing & Brand";
      } else if (
        [
          "Microsoft Excel",
          "Google Sheets",
          "Microsoft PowerPoint",
          "Microsoft Word",
          "Business Writing",
          "Proposal Writing",
          "RFP & Tendering",
        ].includes(option)
      ) {
        group = "Office Productivity";
      } else if (["10th Pass", "12th Pass"].includes(option)) {
        group = "Secondary";
      } else if (
        option.startsWith("Diploma") ||
        option === "Associate Degree"
      ) {
        group = "Diploma & Associate";
      } else if (
        [
          "B.Tech",
          "B.E.",
          "B.Sc (Computer Science)",
          "B.Sc (Information Technology)",
          "B.Sc (Electronics)",
          "B.Sc (Mathematics)",
          "B.Sc (Physics)",
          "B.Sc (Chemistry)",
          "B.Sc (Statistics)",
          "BCA",
          "BBA",
          "B.Com",
          "BA",
          "B.Des",
          "B.Arch",
          "B.Pharm",
          "B.Sc Nursing",
          "LLB",
          "MBBS",
          "BDS",
        ].includes(option)
      ) {
        group = "Bachelor's";
      } else if (
        [
          "M.Tech",
          "M.E.",
          "M.Sc (Computer Science)",
          "M.Sc (Information Technology)",
          "M.Sc (Data Science)",
          "M.Sc (Artificial Intelligence)",
          "M.Sc (Mathematics)",
          "M.Sc (Statistics)",
          "M.Sc (Physics)",
          "M.Sc (Chemistry)",
          "MS",
          "MCA",
          "MBA",
          "M.Com",
          "MA",
          "M.Pharm",
          "MHA",
          "LLM",
        ].includes(option)
      ) {
        group = "Master's";
      } else if (option.startsWith("PhD")) {
        group = "Doctorate";
      } else if (
        [
          "CA (Chartered Accountant)",
          "CS (Company Secretary)",
          "CMA (Cost & Management Accountant)",
          "CFA",
          "CPA",
          "ACCA",
          "PMP",
          "PRINCE2 Foundation",
          "PRINCE2 Practitioner",
          "CSM (Scrum Master)",
          "PSM (Professional Scrum Master)",
          "Six Sigma Green Belt",
          "Six Sigma Black Belt",
          "ITIL Foundation",
          "AWS Certified Cloud Practitioner",
          "AWS Certified Solutions Architect – Associate",
          "AWS Certified Solutions Architect – Professional",
          "AWS Certified Developer – Associate",
          "AWS Certified DevOps Engineer – Professional",
          "Microsoft Azure Fundamentals (AZ-900)",
          "Microsoft Azure Administrator (AZ-104)",
          "Microsoft Azure Solutions Architect (AZ-305)",
          "Google Cloud Digital Leader",
          "Google Professional Cloud Architect",
          "Kubernetes (CKA)",
          "Kubernetes (CKAD)",
          "Docker Certified Associate",
          "Tableau Desktop Specialist",
          "Power BI Data Analyst Associate",
          "Snowflake SnowPro Core",
          "Databricks Lakehouse Fundamentals",
          "Cisco CCNA",
          "Cisco CCNP",
          "CompTIA Security+",
          "Certified Ethical Hacker (CEH)",
          "Red Hat RHCSA",
          "Red Hat RHCE",
          "Salesforce Administrator",
          "Salesforce Platform Developer I",
          "SAP Certified Associate",
          "Oracle Certified Professional",
          "Google UX Design Certificate",
          "Meta Digital Marketing Associate",
        ].includes(option)
      ) {
        group = "Certifications";
      } else if (
        option.startsWith("Certificate") ||
        option.startsWith("Diploma")
      ) {
        group = "Certificates";
      }

      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(option);
    });

    return groups;
  };

  // Filter options based on search value
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Group filtered options
  const groupedOptions = groupOptions(filteredOptions);

  // Check if search value doesn't match any existing options
  const hasNoMatches = searchValue && filteredOptions.length === 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        className={`w-full flex flex-row justify-between items-center min-h-10 text-sm border border-[#c9c9c9] rounded-md px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none  ${className}`}
        ref={triggerRef}
      >
        <span className="text-left">{value || placeholder}</span>
        <RiExpandUpDownLine className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div
          className={`absolute z-50 w-full bg-white border border-[#c9c9c9] rounded-md shadow-lg ${
            placeUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          style={{ maxHeight: dropdownMaxHeight, overflow: "hidden" }}
        >
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder={`Search ${placeholder.toLowerCase()}...`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div
            className="overflow-y-auto"
            style={{ maxHeight: dropdownMaxHeight - 48 }}
          >
            {hasNoMatches ? (
              <div className="p-2">
                <div className="text-sm text-gray-500 mb-2">
                  No {placeholder.toLowerCase()} found.
                </div>
              </div>
            ) : (
              Object.entries(groupedOptions).map(
                ([groupName, groupOptions]) => (
                  <div key={groupName} className="border-b last:border-b-0">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                      {groupName} ({groupOptions.length})
                    </div>
                    {groupOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
                      >
                        <AiOutlineCheck
                          className={`h-4 w-4 ${
                            value === option ? "opacity-100" : "opacity-0"
                          }`}
                        />
                        {option}
                      </button>
                    ))}
                  </div>
                )
              )
            )}
           {searchValue.length > 0 && <button
              type="button"
              onClick={handleAddCustom}
              className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <AiOutlinePlus className="h-4 w-4" />
              Add "{searchValue}"
            </button>}
          </div>
        </div>
      )}
    </div>
  );
};

export { SearchableSelect };
